import React, { useEffect, useRef } from "react";
import "./atmosphere.css";

/** Canvas stand-in for the reference Three.js backdrop: rotating star sphere + orbit rings. */
const Starfield = ({ ready = true }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    document.body.classList.add("grain");
    return () => document.body.classList.remove("grain");
  }, []);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }

    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");

    let frame = 0;
    let width = 0;
    let height = 0;
    let stars = [];
    let mouse = { x: 0, y: 0 };
    let scrollT = 0;
    let heroFade = 1; // rings/glow only in hero — fade out into About+
    let rotY = 0;
    let rotX = 0;
    let ringA = 0;
    let ringB = 0;
    let last = performance.now();
    const root = canvas.closest(".atmosphere");

    const rand = (() => {
      let t = 0xbc2739;
      return () => {
        t = (t + 0x6d2b79f5) >>> 0;
        let e = Math.imul(t ^ (t >>> 15), 1 | t);
        e = (e + Math.imul(e ^ (e >>> 7), 61 | e)) ^ e;
        return ((e ^ (e >>> 14)) >>> 0) / 4294967296;
      };
    })();

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Match reference tiers: ~1300 on typical desktop
      const count = Math.min(2600, Math.max(700, Math.floor((width * height) / 900)));
      rand(); // reset feel by re-seeding walk via count change only
      stars = [];
      for (let i = 0; i < count; i += 1) {
        const theta = rand() * Math.PI * 2;
        const phi = Math.acos(2 * rand() - 1);
        const radius = 3.2 + 5.5 * Math.pow(rand(), 0.6);
        stars.push({
          x: radius * Math.sin(phi) * Math.cos(theta),
          y: radius * Math.sin(phi) * Math.sin(theta) * 0.72,
          z: radius * Math.cos(phi),
          tw: rand() * Math.PI * 2,
          bright: 0.45 + rand() * 0.55,
        });
      }
    };

    const onMove = (event) => {
      mouse.x = (event.clientX / Math.max(width, 1)) * 2 - 1;
      mouse.y = -((event.clientY / Math.max(height, 1)) * 2 - 1);
    };
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      scrollT = max > 0 ? window.scrollY / max : 0;
      // Match reference Backdrop: strong hero visual → quiet after ~40% page
      const y = window.scrollY / Math.max(height, 1);
      if (y <= 0.35) heroFade = 1;
      else if (y >= 1.15) heroFade = 0;
      else heroFade = 1 - (y - 0.35) / 0.8;
      if (root) {
        // Keep a thin starfield later; kill the “photo orb” look in About
        const pageFade = scrollT <= 0.12 ? 1 : scrollT >= 0.4 ? 0.32 : 1 - ((scrollT - 0.12) / 0.28) * 0.68;
        root.style.setProperty("--atmosphere-fade", String(pageFade));
      }
    };

    const project = (x, y, z, cx, cy, scale) => {
      const perspective = 5.6;
      const depth = perspective / (perspective + z);
      return {
        sx: cx + x * scale * depth,
        sy: cy + y * scale * depth,
        depth,
      };
    };

    const rotateY = (p, a) => {
      const c = Math.cos(a);
      const s = Math.sin(a);
      return { x: p.x * c + p.z * s, y: p.y, z: -p.x * s + p.z * c };
    };
    const rotateX = (p, a) => {
      const c = Math.cos(a);
      const s = Math.sin(a);
      return { x: p.x, y: p.y * c - p.z * s, z: p.y * s + p.z * c };
    };

    const drawRing = (cx, cy, scale, tiltX, tiltY, tiltZ, radius, color, alpha) => {
      ctx.beginPath();
      const steps = 160;
      for (let i = 0; i <= steps; i += 1) {
        const t = (i / steps) * Math.PI * 2;
        let p = { x: Math.cos(t) * radius, y: Math.sin(t) * radius, z: 0 };
        p = rotateX(p, tiltX);
        p = rotateY(p, tiltY);
        const c = Math.cos(tiltZ);
        const s = Math.sin(tiltZ);
        const rx = p.x;
        const ry = p.y;
        p = { x: rx * c - ry * s, y: rx * s + ry * c, z: p.z };
        const { sx, sy, depth } = project(p.x, p.y, p.z, cx, cy, scale);
        if (depth <= 0) continue;
        if (i === 0) ctx.moveTo(sx, sy);
        else ctx.lineTo(sx, sy);
      }
      ctx.strokeStyle = `rgba(${color}, ${alpha})`;
      ctx.lineWidth = 1.15;
      ctx.stroke();
    };

    const drawSun = (cx, cy, now, fade) => {
      const pulse = 1 + 0.03 * Math.sin(now * 0.0006);
      const r = Math.min(width, height) * 0.2 * pulse;
      const floatY = Math.sin(now * 0.0008) * 10;

      const corona = ctx.createRadialGradient(cx, cy + floatY, r * 0.2, cx, cy + floatY, r * 2.4);
      corona.addColorStop(0, `rgba(227, 85, 107, ${0.35 * fade})`);
      corona.addColorStop(0.35, `rgba(188, 39, 57, ${0.18 * fade})`);
      corona.addColorStop(0.7, `rgba(188, 39, 57, ${0.05 * fade})`);
      corona.addColorStop(1, "rgba(188, 39, 57, 0)");
      ctx.fillStyle = corona;
      ctx.beginPath();
      ctx.arc(cx, cy + floatY, r * 2.4, 0, Math.PI * 2);
      ctx.fill();

      const core = ctx.createRadialGradient(
        cx - r * 0.32,
        cy + floatY - r * 0.38,
        r * 0.08,
        cx,
        cy + floatY,
        r
      );
      core.addColorStop(0, `rgba(255, 190, 200, ${0.95 * fade})`);
      core.addColorStop(0.18, `rgba(227, 85, 107, ${0.95 * fade})`);
      core.addColorStop(0.45, `rgba(188, 39, 57, ${0.98 * fade})`);
      core.addColorStop(0.78, `rgba(90, 12, 22, ${0.98 * fade})`);
      core.addColorStop(1, `rgba(30, 4, 8, ${0.9 * fade})`);
      ctx.fillStyle = core;
      ctx.beginPath();
      ctx.arc(cx, cy + floatY, r, 0, Math.PI * 2);
      ctx.fill();

      const shine = ctx.createRadialGradient(
        cx - r * 0.28,
        cy + floatY - r * 0.32,
        0,
        cx - r * 0.28,
        cy + floatY - r * 0.32,
        r * 0.45
      );
      shine.addColorStop(0, `rgba(255, 255, 255, ${0.55 * fade})`);
      shine.addColorStop(0.35, `rgba(255, 200, 210, ${0.18 * fade})`);
      shine.addColorStop(1, "rgba(255, 255, 255, 0)");
      ctx.fillStyle = shine;
      ctx.beginPath();
      ctx.arc(cx - r * 0.28, cy + floatY - r * 0.32, r * 0.45, 0, Math.PI * 2);
      ctx.fill();

      const cageRot = now * 0.00015;
      ctx.save();
      ctx.translate(cx, cy + floatY);
      ctx.rotate(cageRot);
      ctx.strokeStyle = `rgba(227, 85, 107, ${0.22 * fade})`;
      ctx.lineWidth = 1;
      for (let i = 0; i < 6; i += 1) {
        const a = (i / 6) * Math.PI;
        ctx.beginPath();
        ctx.ellipse(0, 0, r * 1.28, r * 1.28 * Math.abs(Math.cos(a)), a, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.restore();
    };

    const draw = (now) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;

      // Reference speeds (rad/sec)
      rotY += 0.028 * dt;
      rotX += (scrollT * 0.9 - rotX) * 0.04;
      ringA += 0.16 * dt;
      ringB -= 0.1 * dt;

      const targetParallaxY = mouse.x * 0.22;
      const targetParallaxX = -(mouse.y * 0.16);
      const parallaxY = targetParallaxY;
      const parallaxX = targetParallaxX;

      ctx.clearRect(0, 0, width, height);

      if (heroFade > 0.01) {
        const g1 = ctx.createRadialGradient(
          width * 0.62,
          height * 0.38,
          0,
          width * 0.62,
          height * 0.38,
          Math.min(width, height) * 0.42
        );
        g1.addColorStop(0, `rgba(188, 39, 57, ${0.12 * heroFade})`);
        g1.addColorStop(0.45, `rgba(188, 39, 57, ${0.04 * heroFade})`);
        g1.addColorStop(1, "rgba(188, 39, 57, 0)");
        ctx.fillStyle = g1;
        ctx.fillRect(0, 0, width, height);
      }

      const cx = width * 0.58 + mouse.x * 18;
      const cy = height * 0.42 + mouse.y * -10;
      const scale = Math.min(width, height) * 0.11;

      if (heroFade > 0.01) {
        drawSun(cx, cy, now, heroFade);
        const wobbleA = Math.PI / 2.6 + 0.12 * Math.sin(now * 0.00025);
        const wobbleB = Math.PI / 3.4 + 0.15 * Math.cos(now * 0.0002);
        drawRing(cx, cy, scale, wobbleA, parallaxY * 0.4, ringA, 2.35, "227, 85, 107", 0.45 * heroFade);
        drawRing(cx, cy, scale, 0, wobbleB + parallaxY * 0.3, ringB + Math.PI / 5, 3.05, "188, 39, 57", 0.32 * heroFade);
      }

      // Rotating star sphere
      for (const star of stars) {
        let p = rotateY(star, rotY + parallaxY);
        p = rotateX(p, rotX + parallaxX * 0.35);
        const { sx, sy, depth } = project(p.x, p.y, p.z, cx, cy, scale);
        if (depth <= 0.15) continue;

        const twinkle = 0.55 + Math.sin(now * 0.0018 + star.tw) * 0.45;
        const size = Math.max(0.6, 1.6 * depth * star.bright);
        const alpha = Math.min(0.92, (0.35 + depth * 0.55) * twinkle * star.bright);

        ctx.beginPath();
        ctx.fillStyle = `rgba(240, 223, 226, ${alpha})`;
        ctx.arc(sx, sy, size, 0, Math.PI * 2);
        ctx.fill();

        // Occasional brighter square spark (reference look)
        if (star.bright > 0.88 && depth > 0.55) {
          ctx.fillStyle = `rgba(227, 85, 107, ${0.25 * twinkle})`;
          ctx.fillRect(sx - 1, sy - 1, 2.2, 2.2);
        }
      }

      frame = requestAnimationFrame(draw);
    };

    resize();
    onScroll();
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    frame = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <div className={`atmosphere${ready ? " is-ready" : ""}`} aria-hidden="true">
      <canvas ref={canvasRef} className="atmosphere__stars" />
      <div className="atmosphere__vignette" />
    </div>
  );
};

export default Starfield;
